import escapeStringRegexp from 'escape-string-regexp'
import React, { useEffect, useState } from 'react'
import type { Location } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { LIVE_LIBRARIES } from '@disco/constants/FeatureTypes'
import {
  clientLibraryClientDelete,
  clientLibraryClientsLoad,
  clientLibraryClientUpdate,
  inviteClientLibraryClients,
} from '@disco/state-management/actions/client-library-client-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import { isClientLibrariesTrial } from '@disco/state-management/selectors/user'

import type { ClientLibrary, ClientLibraryClient } from 'types/client-library'
import type { State } from 'types/redux'
import type { PopoverItemType } from 'types/ui'

import emptyClients1x from 'assets/images/empty-clients.png'
import emptyClients2x from 'assets/images/empty-clients@2x.png'

import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import DataTable from 'components/ui/data-table'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import EmptyState from 'components/ui/empty-state'
import Input from 'components/ui/input'
import Lozenge from 'components/ui/lozenge'
import PageContainer from 'components/ui/page-container'
import PricingBadge from 'components/ui/pricing-badge'

import { useDispatch, useSelector } from 'hooks/use-redux'

import LibraryClientName from './library-client-name'

const getLozengeColor = (status: ClientLibraryClient['status']) => {
  switch (status) {
    case 'pending':
    case 'invited':
      return 'yellow'

    case 'rejected':
    case 'rejected supervisor':
      return 'red'

    case 'approved':
    case 'pre-approved supervisor':
      return 'green'

    default:
      return undefined
  }
}

type Props = {
  children: React.ReactNode
  location: Location
}

const LibrariesLibraryClients = (
  props: Props,
): React.ReactNode | React.ReactNode => {
  const dispatch = useDispatch()
  const { libraryId } = useParams<{ libraryId: string }>()
  const libraryIdNum: number = parseInt(libraryId!, 10)
  const library: ClientLibrary | null | undefined = useSelector(
    (state: State) => getClientLibrary(state, libraryIdNum),
  )
  const clientLibrariesTrial: boolean = useSelector(isClientLibrariesTrial)
  const liveLibrariesEnabled: boolean = useFeatureEnabled(LIVE_LIBRARIES)
  useEffect(() => {
    dispatch(clientLibraryClientsLoad(libraryIdNum))
  }, [libraryIdNum])
  const clients = library?.clients
  const [query, setQuery] = useState('')

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) =>
    setQuery(event.target.value)

  const getEllipsisMenuOptions = (client: ClientLibraryClient) => {
    let options: PopoverItemType[] = [
      {
        disabled:
          !liveLibrariesEnabled ||
          client['status'] === 'pre-approved supervisor' ||
          client['status'] === 'rejected supervisor',
        destructive: true,
        label: 'Delete',
        onClick: () =>
          dispatch(clientLibraryClientDelete(libraryIdNum, client['id'])),
      },
    ]

    if (client['status'] === 'invited') {
      options = [
        {
          label: 'Resend invite',
          onClick: () =>
            dispatch(
              inviteClientLibraryClients(
                libraryIdNum,
                {
                  content_message: undefined,
                  clients_list: [
                    {
                      email: client.email,
                      first_name: client.first_name || ' ',
                      last_name: client.last_name || ' ',
                    },
                  ],
                },
                'resend',
              ),
            ),
        },
        ...options,
      ]
    } else {
      options = [
        {
          disabled:
            !liveLibrariesEnabled ||
            client['status'] === 'approved' ||
            client['status'] === 'pre-approved supervisor' ||
            client['status'] === 'rejected supervisor',
          badge:
            !liveLibrariesEnabled && client['status'] !== 'approved' ? (
              <PricingBadge title="Upgrade" />
            ) : null,
          label: 'Approve',
          onClick: () => {
            dispatch(
              clientLibraryClientUpdate(libraryIdNum, client['id'], {
                status: 'approved',
              }),
            )
            mixpanel.sendPureEvent('Invite approved')
          },
        },
        {
          disabled:
            client['status'] === 'rejected' ||
            client['status'] === 'rejected supervisor',
          label: 'Reject',
          onClick: () => {
            dispatch(
              clientLibraryClientUpdate(libraryIdNum, client['id'], {
                status: 'rejected',
              }),
            )
            mixpanel.sendPureEvent('Invite rejected')
          },
        },
        ...options,
      ]
    }

    return options
  }

  let reasonInvitesDisabled = undefined

  if (clientLibrariesTrial) {
    reasonInvitesDisabled = 'You’re using the trial version of Discovery Suite.'
  } else if (!liveLibrariesEnabled) {
    reasonInvitesDisabled =
      'To invite clients, the Catalog needs to be live. Making Catalogs live requires a Discovery Suite subscription.'
  } else if (!library?.is_live) {
    reasonInvitesDisabled =
      'To invite clients to your Catalog, please set the Catalog to Published using the switch at the top right.'
  }

  const InviteButton = () => (
    <Button
      appearance="primary"
      caption="Invite clients"
      disabled={Boolean(reasonInvitesDisabled)}
      link={{
        pathname: `/catalogs/${libraryIdNum}/clients/invite`,
        state: {
          modal: true,
          returnTo: props.location.pathname,
        },
      }}
      tooltip={reasonInvitesDisabled}
    />
  )

  return props.location.state?.modal ? (
    props.children
  ) : (
    <PageContainer>
      <PageContainer.Section
        title="Clients"
        action={clients && clients.list.length > 0 && <InviteButton />}
      >
        {library && !library?.loading && (
          <DocumentHeadTags title={`${library?.name} | Clients | DISCO`} />
        )}
        <Row columnsSize={[3]} marginBottom={false}>
          <div>
            <Input
              icon="search"
              placeholder="Search"
              value={query}
              onChange={updateSearchQuery}
            />
          </div>
        </Row>
        <DataTable
          appearance="plain"
          columns={[
            {
              name: 'Name',
              render: ({
                first_name,
                last_name,
                status,
              }: ClientLibraryClient) => (
                <LibraryClientName
                  first_name={first_name}
                  last_name={last_name}
                  status={status}
                />
              ),
            },
            {
              id: 'email',
              name: 'Email',
              render: (email: string) =>
                email !== 'hidden' && email ? email : '',
            },
            {
              name: 'Status',
              render: ({ status }: ClientLibraryClient) => (
                <Lozenge color={getLozengeColor(status)} text={status} />
              ),
            },
            {
              contentType: 'button',
              name: 'Actions',
              width: 'content',
              render: (client: ClientLibraryClient) => (
                <EllipsisMenu
                  compact
                  options={getEllipsisMenuOptions(client)}
                  icon="ellipsis"
                  hasArrow={false}
                />
              ),
            },
          ]}
          data={
            clients &&
            clients.list.filter(({ first_name, last_name, email }) => {
              const pattern =
                '.*' +
                query
                  .trim()
                  .split('')
                  .map(char => escapeStringRegexp(char))
                  .join('.*') +
                '.*'
              const re = new RegExp(pattern)
              return re.test([first_name, last_name, email].join(' '))
            })
          }
          loading={library?.loading || clients?.loading}
          rows={5}
          emptyState={
            query ? (
              'Sorry! No clients found.'
            ) : (
              <EmptyState>
                <img
                  srcSet={`${emptyClients1x}, ${emptyClients2x} 2x`}
                  width="200"
                  height="183"
                />
                <p>
                  No clients here yet! Invite some people to your Catalog via
                  the button below.
                </p>
                <InviteButton />
              </EmptyState>
            )
          }
        />
        {props.children}
      </PageContainer.Section>
    </PageContainer>
  )
}

export default LibrariesLibraryClients
